<!--
  Same hand-rolled modal pattern as AccountEditDialog.vue - see its own comment. Two steps,
  not myWealth's single click with no confirmation (feature-merchants.md §2.2.3, D-MER-15):
  picking a destination only unlocks a second screen stating how many entries will move and
  that the source is deleted, and only that screen's own button performs the merge.
-->
<script setup lang="ts">
import { ActionButton, Dialog, SearchableSelect } from '@taler/ui'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Merchant, MerchantMergeResult } from '../composables/use-merchant-actions.ts'
import { problemDetail } from '../internal/problem.ts'

const props = defineProps<{
  open: boolean
  source: Merchant | undefined
  candidates: readonly Merchant[]
  countEntries: (sourceId: string, signal?: AbortSignal) => Promise<number>
  merge: (sourceId: string, destinationId: string) => Promise<MerchantMergeResult>
}>()

const emit = defineEmits<{ close: []; merged: [result: MerchantMergeResult] }>()

const { t } = useI18n()

const dialogRef = useTemplateRef<HTMLElement>('dialog')
const destinationId = ref<string | undefined>(undefined)
const step = ref<'select' | 'confirm'>('select')
const entryCount = ref<number | undefined>(undefined)
const isCounting = ref(false)
const isMerging = ref(false)
const mergeError = ref<Error | null>(null)
const countError = ref<Error | null>(null)
let countAbort: AbortController | undefined

watch(
  () => props.open,
  (open) => {
    if (!open) return
    destinationId.value = undefined
    step.value = 'select'
    entryCount.value = undefined
    mergeError.value = null
    countError.value = null
  },
)

const destinationOptions = computed(() =>
  props.candidates
    .filter((candidate) => candidate.id !== props.source?.id)
    .map((candidate) => ({ value: candidate.id, label: candidate.name })),
)

const destination = computed(() => props.candidates.find((candidate) => candidate.id === destinationId.value))

function onDestinationUpdate(value: string | readonly string[]): void {
  destinationId.value = typeof value === 'string' ? value : undefined
}

async function onContinue(): Promise<void> {
  if (props.source === undefined || destinationId.value === undefined) return
  countAbort?.abort()
  countAbort = new AbortController()
  isCounting.value = true
  entryCount.value = undefined
  countError.value = null
  step.value = 'confirm'
  try {
    entryCount.value = await props.countEntries(props.source.id, countAbort.signal)
  } catch (error) {
    countError.value = error as Error
  } finally {
    isCounting.value = false
  }
}

function onBack(): void {
  step.value = 'select'
  countError.value = null
}

async function onConfirmMerge(): Promise<void> {
  if (props.source === undefined || destinationId.value === undefined) return
  isMerging.value = true
  mergeError.value = null
  try {
    const result = await props.merge(props.source.id, destinationId.value)
    emit('merged', result)
  } catch (error) {
    mergeError.value = error as Error
  } finally {
    isMerging.value = false
  }
}

watch(
  () => [props.open, step.value] as const,
  () => {
    if (props.open)
      void nextTick(() => dialogRef.value?.querySelector<HTMLElement>('button, input, select')?.focus())
  },
)
</script>

<template>
  <Dialog v-if="source" :open="open" :label="t('merchants.mergeConfirm')" @close="emit('close')">
    <div ref="dialog" class="flex flex-col gap-[var(--space-cell)] p-[var(--space-block)]">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('merchants.mergeInto', { name: source.name }) }}
      </h2>

      <template v-if="step === 'select'">
        <label class="flex flex-col gap-[var(--space-hair)]">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">
            {{ t('merchants.mergeDestinationLabel') }}
          </span>
          <SearchableSelect
            :model-value="destinationId"
            :options="destinationOptions"
            :trigger-label="t('merchants.mergeDestinationPlaceholder')"
            :search-label="t('merchants.mergeDestinationSearchLabel')"
            :empty-text="t('merchants.mergeDestinationEmpty')"
            @update:model-value="onDestinationUpdate"
          />
        </label>
        <div class="mt-[var(--space-hair)] flex justify-end gap-[var(--space-inline)]">
          <button
            type="button"
            class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[color:var(--color-text)]"
            @click="emit('close')"
          >
            {{ t('merchants.mergeCancel') }}
          </button>
          <ActionButton type="button" :disabled="destinationId === undefined" @click="onContinue">
            {{ t('merchants.mergeContinue') }}
          </ActionButton>
        </div>
      </template>

      <template v-else>
        <p v-if="isCounting" role="status" class="text-[color:var(--color-text-muted)]">
          {{ t('merchants.mergeCounting') }}
        </p>
        <p v-else-if="countError !== null" role="alert" class="text-[color:var(--color-danger)]">
          {{ t('merchants.mergeCountError') }}
        </p>
        <p v-else role="alert" class="text-[color:var(--color-text)]">
          {{
            entryCount === 0
              ? t('merchants.mergeConsequenceNone', {
                  source: source.name,
                  destination: destination?.name ?? '',
                })
              : entryCount === 1
                ? t('merchants.mergeConsequenceOne', {
                    source: source.name,
                    destination: destination?.name ?? '',
                  })
                : t('merchants.mergeConsequenceMany', {
                    count: entryCount,
                    source: source.name,
                    destination: destination?.name ?? '',
                  })
          }}
        </p>

        <p v-if="mergeError !== null" role="alert" class="text-[color:var(--color-danger)]">
          {{ problemDetail(mergeError) ?? t('merchants.mergeError') }}
        </p>

        <div class="mt-[var(--space-hair)] flex justify-end gap-[var(--space-inline)]">
          <button
            type="button"
            :disabled="isMerging"
            class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[color:var(--color-text)] disabled:opacity-60"
            @click="onBack"
          >
            {{ t('merchants.mergeBack') }}
          </button>
          <button
            type="button"
            :disabled="isMerging || isCounting || entryCount === undefined"
            class="min-h-[var(--density-control)] rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-[var(--space-cell)] text-[color:var(--color-surface)] disabled:opacity-60"
            @click="onConfirmMerge"
          >
            {{
              isMerging
                ? t('merchants.mergeSubmitting')
                : t('merchants.mergeConfirmAction', { source: source.name })
            }}
          </button>
        </div>
      </template>
    </div>
  </Dialog>
</template>
